/**
 * Tu dinero disponible (puro): liquidez a fin de cada mes del año, por qué cambió cada mes (ahorro, aportaciones a
 * fondos, otros), el saldo de cada cuenta mes a mes (tu parte) y el colchón frente a lo que gastas.
 *
 * - Liquidez = cuentas del patrimonio, por tu parte (lo mismo que en Patrimonio). El mes en curso, a hoy.
 * - Colchón = liquidez SIN las cuentas de comida (Tickets: solo sirven para comer) ÷ gasto medio de los 12 meses
 *   cerrados anteriores. La línea de referencia es el mínimo habitual: 3 meses de gasto.
 * - Meses anteriores al primer movimiento del libro (o ya archivados): la liquidez sale de la foto de patrimonio y
 *   el detalle por cuenta no se conoce.
 */
import { cashAt, contributionsIn, monthEnd, monthTotals, prevMonth, round2 } from '@pf/domain';
import { effectiveSnapshots, type Computed } from './model.js';
import { monthlyExpenseAvg } from './insights.js';
import { accountUse } from './account-use.js';
import { poolMineAt, poolMode } from './account-pool.js';

/** Colchón recomendado, en meses de gasto. */
export const CUSHION = { min: 3, max: 6 };

/** Cuenta de comida (Tickets/Pluxee y similares): es liquidez, pero no sirve de colchón. */
export const isMealAccount = (a: { name: string; type?: string; use?: string }) => accountUse(a).use === 'limited';

/** Liquidez: tus cuentas del patrimonio que no están apartadas para pagos (esas son tuyas, pero no están disponibles). */
const accountsOf = (c: Computed) => c.wb.accounts.filter(a => a.inNetWorth && accountUse(a).use !== 'reserved');
/** Apartadas para pagos: se enseñan aparte (fuera del gráfico y del colchón). */
const reservedOf = (c: Computed) => c.wb.accounts.filter(a => a.inNetWorth && accountUse(a).use === 'reserved');
const balance = (c: Computed, name: string, date: string) => {
  const a = c.wb.accounts.find(x => x.name === name)!;
  const raw = a.opening + a.archived + c.wb.movements.filter(m => m.account === name && m.date && m.date <= date).reduce((t, m) => t + m.amount, 0);
  return round2(raw * (a.share || 1));
};

/** Liquidez que cuenta como colchón a una fecha: tus cuentas del patrimonio sin las de comida, por tu parte. */
export function cushionCash(c: Computed, date: string): number {
  return round2(accountsOf(c).filter(a => !isMealAccount(a)).reduce((t, a) => t + balance(c, a.name, date), 0));
}

/** Meses de gasto que cubre el colchón (null con menos de 3 meses de historia). */
export function cushionMonths(c: Computed, month: string, date: string): number | null {
  const avg = monthlyExpenseAvg(c, month);
  return avg ? Math.round(cushionCash(c, date) / avg * 10) / 10 : null;
}

export interface LiquidityPoint {
  month: string; cash: number; live: boolean;
  /** Cambio respecto al mes anterior (null si no se conoce). */
  change: number | null;
  /** Ahorro del mes (ingresos − gastos, como en Mes a mes). */
  savings: number;
  /** Aportado a fondos ese mes (negativo si se sacó). */
  invested: number;
  /** El resto del cambio: traspasos con cuentas fuera del patrimonio, ajustes, desfases… */
  other: number | null;
}
export interface LiquidityAccount { name: string; share: number; meal: boolean;
  /** Apartada para pagos (y para qué): fuera del gráfico y del colchón. */
  reserved: boolean; purpose: string;
  /** Tu parte según lo que aporta cada persona (no un % del saldo). */
  contrib: boolean; values: Array<number | null>; start: number | null; now: number; change: number | null }
export interface LiquidityYear {
  months: string[]; points: LiquidityPoint[];
  /** Liquidez al empezar el año (31 dic anterior) y ahora (hoy o fin del último mes). */
  start: number | null; now: number | null; change: number | null;
  avg: number | null; cushionCash: number; cushion: number | null;
  /** Línea de colchón mínimo: 3 meses de gasto medio. */
  threshold: number | null;
  /** Lo que hay en cuentas de comida (fuera del colchón). */
  meal: number;
  accounts: LiquidityAccount[];
}

export function liquidityYear(c: Computed, year: number, today: string): LiquidityYear {
  const current = today.slice(0, 7);
  const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`).filter(m => m <= current);
  const firstMonth = c.wb.movements.reduce((m, x) => (x.date && (!m || x.date < m) ? x.date : m), '').slice(0, 7);
  // Desde el mes anterior al primer movimiento (saldos iniciales) se puede calcular con los movimientos
  const known = (m: string) => !!firstMonth && m >= prevMonth(firstMonth);
  const dateOf = (m: string) => (m === current ? today : monthEnd(m));
  const snap = new Map(effectiveSnapshots(c).map(s => [s.month, s]));
  const accounts = accountsOf(c);
  const all = accounts.map(a => ({ name: a.name, share: a.share || 1, inNetWorth: true, opening: a.opening + a.archived }));
  const movs = c.wb.movements.map(m => ({ account: m.account, date: m.date, amount: m.amount }));
  // El cierre apuntado en Patrimonio incluye las cuentas apartadas para pagos: aquí no cuentan (su parte se resta)
  // (calculado: con tu parte de verdad en las de «según lo que aporta cada persona»; apuntado a mano: con el % del saldo)
  const reservedAt = (date: string, manual: boolean) => round2(reservedOf(c).reduce((t, a) =>
    t + (!manual && poolMode(a.split) === 'contrib' ? poolMineAt(c, a.name, date) : balance(c, a.name, date)), 0));
  const cashOf = (m: string): number | null => {
    if (m === current) return cashAt(all, movs, today);
    const s = snap.get(m);
    return s !== undefined ? round2(s.cash - reservedAt(monthEnd(m), s.source === 'manual')) : known(m) ? cashAt(all, movs, monthEnd(m)) : null;
  };

  const prevDec = `${year - 1}-12`;
  const start = cashOf(prevDec);
  const points: LiquidityPoint[] = [];
  let prev = start;
  for (const m of months) {
    const cash = cashOf(m);
    if (cash === null) { prev = null; continue; }
    const change = prev === null ? null : round2(cash - prev);
    const savings = round2(monthTotals(c.items, m).savings);
    const invested = round2(contributionsIn(c.wb.flows, m));
    points.push({ month: m, cash, live: m === current, change, savings, invested, other: change === null ? null : round2(change - savings + invested) || 0 });
    prev = cash;
  }
  const last = points[points.length - 1];
  const now = last?.cash ?? null;
  const ref = months[months.length - 1] ?? current;
  const avg = monthlyExpenseAvg(c, ref);
  const cushionAt = dateOf(ref);
  const cc = cushionCash(c, cushionAt);
  return {
    months, points, start, now, change: start !== null && now !== null ? round2(now - start) : null,
    avg, cushionCash: cc, cushion: avg ? Math.round(cc / avg * 10) / 10 : null, threshold: avg ? round2(avg * CUSHION.min) : null,
    meal: round2(accounts.filter(isMealAccount).reduce((t, a) => t + balance(c, a.name, cushionAt), 0)),
    accounts: [...accounts, ...reservedOf(c)].map(a => {
      // Apartada con «tu parte según lo que aporta cada persona»: tu parte de verdad, no el % del saldo
      const bal = accountUse(a).use === 'reserved' && poolMode(a.split) === 'contrib' ? (d: string) => poolMineAt(c, a.name, d) : (d: string) => balance(c, a.name, d);
      const values = months.map(m => (known(m) ? bal(dateOf(m)) : null));
      const s = known(prevDec) ? bal(monthEnd(prevDec)) : null;
      const n = values[values.length - 1] ?? bal(cushionAt);
      const u = accountUse(a);
      return { name: a.name, share: a.share || 1, meal: u.use === 'limited', reserved: u.use === 'reserved', purpose: u.purpose, contrib: u.use === 'reserved' && poolMode(a.split) === 'contrib', values, start: s, now: n, change: s === null ? null : round2(n - s) };
    }).sort((x, y) => Number(x.reserved) - Number(y.reserved) || Number(x.meal) - Number(y.meal) || y.now - x.now),
  };
}
